import React from "react";
import { Modal } from "react-bootstrap";

import useEsTelefono from "../../../../utils/esTelefono";
import { ArticleDTO, KitEnrollmentContext } from "../../../../models/ArticleDTO";
import { kitDeliveryStatus, owedArticleStatus } from "../../../../constants";
import { shortName } from "../kits/shared";
import "./ventas.css";

/* Piezas comunes de Ventas y Entregas (Módulos Claros, 2026-11-01). */

const num = (n: number | null | undefined) => (Math.round((Number(n) || 0) * 100) / 100).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Monto con el símbolo del plantel: «$1,200.00» en pesos, «L 1,200.00» en lempiras. */
export const monto = (sym: string, n: number | null | undefined) => (/^[A-Za-z]/.test(sym) ? sym + " " : sym) + num(n);

/** «Sáb 08:00 - 11:30» → «Sáb 8:00 am - 11:30 am»: el backend arma los horarios en 24 h y aquí van en am/pm. */
export const ampm = (texto: string | null | undefined): string => (texto || "").replace(/\b(\d{1,2}):(\d{2})\b(?!\s*[ap]\.?\s*m)/gi, (_m, h, mm) => {
	const n = Number(h);
	return (n % 12 === 0 ? 12 : n % 12) + ":" + mm + (n < 12 ? " am" : " pm");
});

/** Ventana de detalle como la de Matrículas y Promociones. enforceFocus apagado: Registrar Entrega, Anular y Reportar Pago se abren encima. */
export const Ventana: React.FC<{ titulo: React.ReactNode; onClose: () => void; busy?: boolean; pie?: React.ReactNode; children: React.ReactNode }> = ({ titulo, onClose, busy, pie, children }) => {
	const movil = useEsTelefono();
	return (
		<Modal className="waModulo wvtVentana" show onHide={busy ? undefined : onClose} size="lg" centered scrollable fullscreen="sm-down" enforceFocus={false}>
			<Modal.Header closeButton={!busy}>
				<Modal.Title style={{ fontSize: movil ? 15 : 18, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>{titulo}</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 15, paddingLeft: movil ? 14 : 22, paddingRight: movil ? 14 : 22 }}>{children}</Modal.Body>
			{pie ? <Modal.Footer className="wvtPie">{pie}</Modal.Footer> : null}
		</Modal>
	);
};

export const Dato: React.FC<{ t: string; children: React.ReactNode }> = ({ t, children }) => <div className="wvtDato"><small>{t}</small><div>{children}</div></div>;
export const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="wvtSub">{children}</div>;
export const Chip: React.FC<{ tono?: "oro" | "acento" | "tenue"; children: React.ReactNode }> = ({ tono, children }) => <span className={"wvtChip" + (tono ? " " + tono : "")}>{children}</span>;

/** Pago de un cargo: Pagado · Con Abono · Sin Pago · Anulado. */
export const pagoDe = (kit: ArticleDTO): { texto: string; tono?: "oro" | "tenue" } => {
	if (kit.isCancelled || kit.statusId === owedArticleStatus.CANCELLED) return { texto: "Anulado", tono: "tenue" };
	if (kit.statusId === owedArticleStatus.FULLY_PAID || kit.statusId === owedArticleStatus.DELIVERED) return { texto: "Pagado" };
	if (kit.statusId === owedArticleStatus.PARTLY_PAID) return { texto: "Con Abono", tono: "oro" };
	return { texto: "Sin Pago", tono: "oro" };
};
export const PagoChip: React.FC<{ kit: ArticleDTO }> = ({ kit }) => { const p = pagoDe(kit); return <Chip tono={p.tono}>{p.texto}</Chip>; };

/** Entrega: lo pendiente en oro; «Entregado» neutro (nunca verde). */
export const entregaDe = (statusId: number): { texto: string; tono?: "oro" | "tenue" } =>
	statusId === kitDeliveryStatus.DELIVERED ? { texto: "Entregado" }
		: statusId === kitDeliveryStatus.PARTIAL ? { texto: "Entrega Parcial", tono: "oro" }
		: statusId === kitDeliveryStatus.PENDING ? { texto: "Sin Entregar", tono: "oro" }
		: { texto: "Sin Registro", tono: "tenue" };
export const EntregaChip: React.FC<{ kit: ArticleDTO }> = ({ kit }) => {
	if (kit.isCancelled) return <span className="wvtSub">-</span>;
	const e = entregaDe(kit.deliveryStatusId);
	return <Chip tono={e.tono}>{e.texto}</Chip>;
};

/** Quién compró: la alumna (o el comprador de una Venta a Terceros). */
export const Compro: React.FC<{ kit: ArticleDTO; enrollment?: KitEnrollmentContext | null }> = ({ kit, enrollment }) => (
	<div style={{ minWidth: 160 }}>
		<b>{kit.isThirdParty ? (kit.buyerName || "Sin Dato") : (kit.studentName || "-")}</b>
		<Sub>{kit.isThirdParty ? ["Venta a Terceros", kit.buyerPhone].filter(Boolean).join(", ")
			: enrollment ? [enrollment.courseBrief || enrollment.module || enrollment.level, ampm(enrollment.schedule)].filter(Boolean).join(", ") : kit.studentCode}</Sub>
	</div>
);

export const Producto: React.FC<{ kit: ArticleDTO }> = ({ kit }) => <>
	{kit.name || kit.title}{kit.quantity > 1 ? <span className="wvtSub" style={{ display: "inline" }}> × {kit.quantity}</span> : null}
</>;

/** Vendido Por: nombre corto de cada quien, con su parte si la venta se repartió. */
export const vendidoPor = (kit: ArticleDTO): string => (kit.advisors || []).length < 1 ? "Sin Vendedor"
	: kit.advisors.map((a) => shortName(a.name) + (a.sharePercentage !== null && a.sharePercentage !== undefined && kit.advisors.length > 1 ? " " + Math.round(a.sharePercentage) + " %" : "")).join(", ");

/** Confirmación «Qué Va a Pasar» antes de anular o reactivar un cargo (patrón de Actualizaciones: resumen y luego el botón). */
export const QueVaAPasar: React.FC<{ titulo: string; lineas: { texto: string; oro?: boolean }[]; motivo: string; onMotivo: (v: string) => void;
	confirmar: string; busy?: boolean; error?: string | null; onCancel: () => void; onConfirm: () => void }> = ({ titulo, lineas, motivo, onMotivo, confirmar, busy, error, onCancel, onConfirm }) => (
	<Modal show onHide={() => { if (!busy) onCancel(); }} centered className="waModulo" style={{ zIndex: 2100 }} backdropClassName="wvtFondoAlto">
		<Modal.Header closeButton={!busy}><Modal.Title as="h5">{titulo}</Modal.Title></Modal.Header>
		<Modal.Body style={{ textAlign: "left" }}>
			<div style={{ fontWeight: 700, fontSize: 13, color: "var(--wa-apagado)", marginBottom: 6 }}>Qué Va a Pasar</div>
			<div className="wvtQue">{lineas.map((l, i) => <div key={"l" + i} className={l.oro ? "oro" : ""}>{l.texto}</div>)}</div>
			<div style={{ fontWeight: 700, fontSize: 13, color: "var(--wa-apagado)", margin: "12px 0 4px" }}>Motivo</div>
			<textarea className="form-control" rows={2} value={motivo} maxLength={2000} disabled={busy} style={{ fontSize: 16 }} onChange={(e) => onMotivo(e.target.value)} />
			{error ? <div className="alert alert-danger" style={{ marginTop: 10, marginBottom: 0, fontSize: 14 }}>{error}</div> : null}
		</Modal.Body>
		<Modal.Footer>
			<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" onClick={onCancel} disabled={busy}>Regresar</button>
			<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || motivo.trim().length < 3} onClick={onConfirm}>{busy ? "Guardando…" : confirmar}</button>
		</Modal.Footer>
	</Modal>
);
